// RunProvenance — which model a box ACTUALLY drove, reported by the box itself (#2140).
//
// The sealed assignment credential is NOT the answer to "what ran this PR". A box can
// fall back to a different provider MID-RUN — prod carries `command` events whose
// stderr reads "subscription lane unusable — switching to fallback provider ..." — so
// the model named at claim time is what we INTENDED, and this event is what actually
// answered. Hence `reason`, and hence the plural: a run emits ONE OF THESE PER SWITCH,
// and both consumers (the read path in `@developerz/db`, the PR-body projection in
// `apps/worker`) must preserve INSERTION ORDER. The last one shipped the diff; the
// earlier ones are why it is not the model the assignment promised.

import { z } from 'zod';
import { LlmTierSourceSchema } from './llm-credential';
import { RunReasoningSchema } from './reasoning-capability';

/**
 * The `task_run_events.kind` this payload rides on — a kind of its own, not a `status`
 * sub-event like `run_stage` / `review_posted`. Two reasons: provenance is not a
 * lifecycle ping (it moves no task and marks no stage), and the read path filters a
 * cheap indexed column instead of scanning `payload ->> 'event'` across the ~31k
 * `status` rows that dominate the table in prod.
 *
 * Spelled ONCE, here: `TaskRunEventKind.RunProvenance` (`./task-run-event`) imports it
 * rather than restating the literal, because that enum is the closed gate the runner's
 * publish and the mothership's pull both validate against — two spellings of one wire
 * token is how a producer and a consumer drift apart in silence.
 */
export const RUN_PROVENANCE_EVENT_KIND = 'run_provenance';

/**
 * Per-field cap. The longest id we actually ship is
 * `us.anthropic.claude-3-5-sonnet-20241022-v2:0` (44 chars), so 100 leaves room for a
 * provider-prefixed BYOK id without ever letting three fields put more than 300 chars
 * into a public PR body. One bound for all three fields on purpose: providers and
 * harnesses are strictly shorter than models, so a second constant would only be a
 * second place to look.
 */
export const RUN_PROVENANCE_TOKEN_MAX = 100;

/**
 * Alphanumeric segments joined by single `.` `_` `-` `:` `/`. No leading, trailing or
 * doubled separator.
 *
 * The values are reported by an untrusted box and rendered VERBATIM into a markdown PR
 * body on a repo we do not own, so the grammar is closed rather than merely bounded:
 *
 * - no whitespace ⇒ the field cannot carry a sentence (an instruction aimed at whatever
 *   reads the PR next — a reviewer bot, or a maintainer's own agent);
 * - the omitted characters are the markdown/HTML surface — `` ` `` `*` `_`-runs `~`
 *   `[` `]` `(` `)` `<` `>` `|` `#` `!` `&` `\` — so the value cannot open a code fence,
 *   a link, an image, a table cell or a tag;
 * - GFM's four autolink-literal shapes are all unspellable: `http(s)://`, `ftp://` and
 *   `mailto:` all need `//` or an `@`, and a separator here must be followed by an
 *   alphanumeric, so `://` cannot occur; `@` is not in the alphabet, which also closes
 *   the bare-email autolink; and the `www.` literal is refused by name below.
 *
 * KNOWN REFUSED, stated so it is diagnosed rather than rediscovered: a Vertex-style
 * `model@version` id (`claude-3-5-sonnet-v2@20241022`). `@` stays out because it is
 * half of the email autolink; if we ever dial Vertex, admit it HERE and escape the
 * rendered value at the same time — never one without the other.
 */
const PROVENANCE_TOKEN = /^[A-Za-z0-9]+(?:[._:/-][A-Za-z0-9]+)*$/;

/** GFM autolinks a bare `www.` host with no scheme; the token grammar alone admits one. */
const BARE_WWW_HOST = /^www\./i;

/**
 * `runner_version` alone, and only because a real version is `0.0.6+sha-d733a83` —
 * semver build metadata, and literally what `runners.runner_version` holds on every
 * enrolled box. {@link PROVENANCE_TOKEN} has no `+`, so it would refuse EVERY version the
 * fleet can report, which is a field nothing can ever write.
 *
 * `+` is the only addition and it costs nothing the grammar was bought for: it carries no
 * markdown or HTML surface inline, it is not part of any GFM autolink shape, and the first
 * character is alphanumeric by construction — so a value can never open a `+ ` list item
 * either. Every other property of the closed grammar is unchanged.
 */
const RUNNER_VERSION_TOKEN = /^[A-Za-z0-9]+(?:[._:/+-][A-Za-z0-9]+)*$/;

function provenanceToken(
  field: string,
  pattern: RegExp = PROVENANCE_TOKEN,
  separators = '. _ - : /',
) {
  return z
    .string()
    .trim()
    .min(1, `${field}: required`)
    .max(RUN_PROVENANCE_TOKEN_MAX, `${field}: at most ${RUN_PROVENANCE_TOKEN_MAX} characters`)
    .regex(
      pattern,
      `${field}: alphanumeric segments joined by ${separators} (no spaces, no markup)`,
    )
    .refine((v) => !BARE_WWW_HOST.test(v), `${field}: must not be a bare host name`);
}

/**
 * The version field, spelled ONCE and used by both obligations below.
 *
 * `RUNNER_VERSION_TOKEN` rather than {@link PROVENANCE_TOKEN}: a real version is
 * `0.0.6+sha-d733a83` and the base grammar has no `+`, so it would refuse every version
 * the fleet can report.
 */
const RUNNER_VERSION = provenanceToken('runner_version', RUNNER_VERSION_TOKEN, '. _ - : / +');

/**
 * WHO PAID FOR THE CALL, spelled ONCE and used by both obligations below (#3828).
 *
 * `account_key` = the account's sealed `llm_credential`. `box_env` = the box's own
 * `DZ_BACKEND_*` environment. This is not a second name for `model`/`provider`, which say
 * WHAT answered; the two are independent, and a row can name the same model under either
 * funding source.
 *
 * A CLOSED ENUM, NEVER A BOOLEAN. A boolean pins the field at exactly two arms forever, and
 * the funding sources are a list this platform can extend; an enum grows a third arm without
 * changing what any stored row means, and — `reason` above being the precedent — a closed
 * arm set is what lets a consumer switch on it exhaustively. `null` is NOT a third arm and
 * must never be published as one: it is reserved for the version skew stated on the read
 * field, and a nullable that also meant "unknown source" would collapse the fact this field
 * exists to carry.
 */
const CREDENTIAL_SOURCE = z.enum(['account_key', 'box_env']);

/**
 * The model the provider actually SERVED (#3287) — same closed grammar as `model`,
 * because it is rendered into the same PR body. It is PROVIDER-authored and arrives
 * at runtime, so a gateway spelling outside the grammar must cost the FIELD, never
 * the event: {@link coerceServedModel} is the stamp-side gate the box runs first,
 * so the schema only ever sees a value this token accepts or `null`.
 */
const SERVED_MODEL_TOKEN = provenanceToken('served_model');

/**
 * WHICH POOL KEY FUNDED THE CALL (plan 102 slice 01) — the sealed credential's
 * `key_id`, a `byok_keys` row id and never key material. Same closed grammar as the
 * other tokens: the row is box-reported, and a row id needs no markup surface.
 * `null` for a `box_env` call (no pool key was involved).
 */
const KEY_ID = provenanceToken('key_id');

/**
 * Fit a provider-reported served model to the `served_model` grammar, or `null`.
 *
 * The served id comes off the response body at runtime and is not the box's to
 * shape — a router that answers `accounts/foo/models/bar` or any value the closed
 * grammar refuses must not fail the whole provenance row (which is a public PR
 * body): the caller passes the result here, warns on a demotion, and emits with
 * `served_model: null` rather than losing the row's attribution entirely. `null`
 * and `undefined` pass through as `null` — "no response was seen".
 */
export function coerceServedModel(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  return SERVED_MODEL_TOKEN.safeParse(raw).success ? raw : null;
}

/**
 * WHAT MAY BE FOUND IN STORAGE — the READ obligation, and the one `@developerz/db`
 * `listRunProvenanceForRun` parses rows with.
 *
 * Unknown keys are STRIPPED, never refused: the read path treats a failed parse as a
 * contract violation and throws, so a box that later stamps an extra field (token counts,
 * a latency) must not be able to fail an entire PR projection with an ADDITION. Removing
 * or retyping a field here still breaks loudly, which is the direction that deserves to.
 *
 * ONE SHAPE, TWO OBLIGATIONS — not two generations and not a dual read. Every field is
 * declared here and {@link RunProvenancePublishSchema} narrows exactly four of them —
 * `runner_version`, `credential_source`, `key_id` and `resolved_by` — so a reader never has to ask which of the two
 * SCHEMAS carries the field list. The obligations differ because the SEAMS differ: a
 * publisher that cannot name its own version, or who paid for the call, is a bug and must
 * fail where the bad value enters, while a reader is not the seam that can repair a row
 * that already exists, and throwing there validates three layers down on data nobody can
 * fix.
 */
export const RunProvenanceSchema = z.object({
  /** The model id we ASKED for / resolved, e.g. `claude-opus-5`. Meaning unchanged (#3287). */
  model: provenanceToken('model'),
  /**
   * The model the provider actually SERVED, off the response body (#3287), or `null`
   * when no response was seen — a failure arm, or a box that predates this field. The
   * router pools accounts and answers from whichever is healthy, so `model` (asked-for)
   * is not always the id that ran; a capacity/cost analysis keyed on `model` alone was
   * wrong by an unknown amount whenever the router substituted. Nullable on BOTH schemas,
   * UNLIKE `runner_version`: a missing response is a legitimate state, not a publisher
   * bug, so it is never narrowed on the write side. `.default(null)` normalises the
   * absent key and an explicit null to one value on the read.
   */
  served_model: SERVED_MODEL_TOKEN.nullable().default(null),
  /** Wire provider that served it, e.g. `anthropic` / `z.ai`. */
  provider: provenanceToken('provider'),
  /** The coding adapter that drove it, e.g. `claude-code`. */
  harness: provenanceToken('harness'),
  /**
   * `selected` = the lane the assignment asked for. `fallback` = the box switched away
   * from it mid-run; the PR body says so, because a maintainer reading "opus" when a
   * fallback model wrote the diff is being told the wrong thing.
   */
  reason: z.enum(['selected', 'fallback']),
  /**
   * THE VERSION OF THE BINARY THAT WROTE THIS ROW — never the version the box is on now.
   *
   * Those are different facts and the difference is measurable. `runners.runner_version`
   * is the only version the mothership held, and it is overwritten on every heartbeat: a
   * fleet converges by PULLING before work, so during a roll the row a `0.0.5` box wrote
   * an hour ago is read against the `0.0.6` the same box now reports, and every defect in
   * the retired binary is attributed to the one that replaced it. Measured 2026-09-02:
   * 1,857 `run_provenance` rows over 30 days carrying exactly four keys, and no key
   * anywhere in `task_run_events` matching `%version%` — nothing recorded which binary
   * produced anything, so "unparseable per version" could only ever answer with today's.
   *
   * `string | null` ON THE READ, AND `null` IS AN ANSWER: "written by a box that predates
   * the stamp". The `.default(null)` is a NORMALISATION, not a fallback — it exists so the
   * absent key and the explicit null reach a caller as ONE value, because zod does not
   * treat them alike and a caller forced to test both would be the third state this field
   * must not have. It repairs nothing and hides no bug: the only producer that can omit the
   * key is one {@link RunProvenancePublishSchema} already refused.
   *
   * WHY THE READ MUST BE LENIENT, and it is a ROLLOUT hazard rather than a backfill one.
   * The mothership is k3s and rolls minutes after merge; the fleet is pull-before-work and
   * converges in ~75 minutes plus maturity. For that whole window EVERY enrolled box is
   * still emitting the four-key payload while the strict consumer is already live, so a
   * required-on-read field takes the PR-body projection down for the length of the roll —
   * on rows the fleet is writing right now, not only on archived ones. Prod writes 100-330
   * of these a day. That is `boot-fatal-gates-on-a-self-updating-fleet`, and the ordering
   * is not fixable by merging in a different order.
   */
  runner_version: RUNNER_VERSION.nullable().default(null),
  /**
   * WHO FUNDED THE CALL THIS ROW DESCRIBES (#3828) — see {@link CREDENTIAL_SOURCE} for why
   * it is a closed enum and never a boolean.
   *
   * `harness` looks like it already answers this and is about to stop. Measured over the 7
   * days to 2026-09-12: 695 rows — 681 `harness: 'native'`, every one funded by the box's
   * own `DZ_BACKEND_*`, and 14 `harness: 'review-brain'`, every one funded by the account's
   * sealed key. The separation is an ACCIDENT of which lanes emit today: the scout publishes
   * `NATIVE_HARNESS_PROGRAM` (`native`) on an ACCOUNT-KEY call, so the first scout row that
   * lands makes `native` mean both. These fields are rendered VERBATIM into a public PR body
   * and into `apps/worker`'s stacked-PR links, so until this field exists the customer is
   * told which model wrote their diff and never who paid for it.
   *
   * `'account_key' | 'box_env' | null` ON THE READ, AND `null` IS AN ANSWER: "written by a
   * box that predates the field". Exactly one meaning, and the `.default(null)` is the same
   * NORMALISATION `runner_version` carries — zod does not treat an absent key and an explicit
   * null alike, and a caller forced to test both would be reading a third state this field
   * must not have. It repairs nothing and hides no bug: the only producer that can omit the
   * key is one {@link RunProvenancePublishSchema} already refused.
   *
   * WHY THE READ MUST BE LENIENT is the ROLLOUT, identically to `runner_version` above and
   * for the same mechanism, not merely the same reasoning. This schema is a bare `z.object`,
   * so a row carrying the key parses under the OLD consumer (unknown keys are stripped) —
   * but a row LACKING it must parse under the NEW one, and the mothership rolls minutes
   * after merge while the fleet is pull-before-work and converges in ~75 minutes plus
   * maturity. For that whole window every enrolled box is still emitting a payload without
   * this key into a consumer that is already live, on rows the fleet is writing right now.
   * A required-on-read field would take the PR-body projection down for the length of every
   * roll — `boot-fatal-gates-on-a-self-updating-fleet`, and the ordering is not fixable by
   * merging in a different order.
   *
   * REQUIRED ON THE WRITE, AND THE GUARD COMES FREE. `apps/runner`'s
   * `runProvenanceCandidate` takes `Omit<RunProvenancePublish, 'runner_version'>`, so
   * narrowing this field on {@link RunProvenancePublishSchema} FAILS THE BUILD at every
   * emitter until each names its own source. The four that exist when this landed:
   * `adapter/index.ts` (box env — it resolves from `runEnv`, the box's `DZ_BACKEND_*`),
   * `pipeline/brain.ts`, `scout/brain/provenance.ts` and
   * `review-threads/provenance.ts` (account key — each resolves the sealed
   * `llm_credential`). That is the same "adding a case FAILS THE BUILD" property
   * `ORIGIN_READS_LLM_CREDENTIAL` relies on, and it is the reason this field belongs to the
   * domain type rather than to a runner-local shape: a fifth emitter cannot be added without
   * answering the question.
   */
  credential_source: CREDENTIAL_SOURCE.nullable().default(null),
  /**
   * WHICH KEY SERVED THE CALL (plan 102 slice 01) — {@link KEY_ID}. Before this field
   * "which key served this run" had no stored answer anywhere, so per-key run share for
   * an account could not be counted. `null` on the read means a `box_env` call OR a box
   * predating the field — the `.default(null)` is the same rollout NORMALISATION as
   * `credential_source` above, for the same mechanism (the fleet converges ~75 minutes
   * after the mothership), and `credential_source` tells the two apart.
   */
  key_id: KEY_ID.nullable().default(null),
  /**
   * WHICH RUNG PICKED THE MODEL (plan 102 slice 01) — the sealed credential's own
   * `resolved_by` (`LlmTierSource`), verbatim. `null`: a `box_env` call, an
   * untier-routed credential, or a box predating the field. Lenient on the read for the
   * rollout, exactly as `key_id`.
   */
  resolved_by: LlmTierSourceSchema.nullable().default(null),
  /**
   * WHAT THE RUN SENT ABOUT REASONING (owner, 2026-09-28: "if provider + model support,
   * it should be always default high") — {@link RunReasoningSchema}: the effective
   * effort, the provider spelling, the control, who decided it (a route override or the
   * platform default) and, when nothing was sent, why. The owner's evidence the default
   * took effect; `reasoning_tokens` on the run's `usage` rows is its cost. `null` on the
   * read = a box predating the field — the same rollout NORMALISATION as `key_id`.
   */
  reasoning: RunReasoningSchema.nullable().default(null),
});
export type RunProvenance = z.infer<typeof RunProvenanceSchema>;

/**
 * WHAT A BOX MUST EMIT TODAY — the WRITE obligation, and the one `apps/runner`'s adapter
 * builds its payload through.
 *
 * Derived from {@link RunProvenanceSchema} so there is exactly one field list and the two
 * cannot drift: this narrows `runner_version`, `credential_source`, `key_id` and
 * `resolved_by`, and states nothing else (plus the one cross-field rule: the funding
 * source and the key agree). A publisher that cannot name its own version, or who paid for the call, fails HERE,
 * at the seam the bad value enters, which is the whole reason the read may be lenient
 * without the attribution hole reopening — an optional field on the write side is a field
 * the next box simply omits, and the hole is then permanent in storage.
 */
export const RunProvenancePublishSchema = RunProvenanceSchema.extend({
  /** Required, non-null: the box knows its own version, and a box that does not is a bug. */
  runner_version: RUNNER_VERSION,
  /**
   * Required, non-null: the emitter resolved the credential it called with, so it knows
   * which one it was. A publisher that cannot name its funding source is a bug, and `null`
   * here would mean "unknown source" — a second meaning for the value the read reserves for
   * version skew, and the exact collapse this field was added to end.
   */
  credential_source: CREDENTIAL_SOURCE,
  /**
   * Required KEY, nullable VALUE (plan 102 slice 01): the emitter holds the sealed
   * credential it called with, or knows it holds none, so it must say which — an
   * omitted key is refused like an omitted `credential_source`. `null` is the
   * `box_env` answer, and the refinement below ties the two together.
   */
  key_id: KEY_ID.nullable(),
  /** Required key, nullable value: the sealed credential's `resolved_by`, or null. */
  resolved_by: LlmTierSourceSchema.nullable(),
  /**
   * Required, non-null: every emitter built the model it called, so it knows what that
   * model was told about reasoning — including "nothing, and why".
   */
  reasoning: RunReasoningSchema,
}).refine(
  (v) =>
    v.credential_source === 'account_key'
      ? v.key_id !== null
      : v.key_id === null && v.resolved_by === null,
  {
    message:
      'run_provenance: an account_key call names its key_id; a box_env call names no key_id and no resolved_by',
    path: ['key_id'],
  },
);
export type RunProvenancePublish = z.infer<typeof RunProvenancePublishSchema>;
